import { formatCurrency } from 'utils/intl';
import dayjs from 'dayjs';
import {
  formatDateTime,
  formatUserDocument,
  getBankName,
  getFormattedTransferType,
} from 'pages/Balance/sections/TableResults/utils';
import { getAccountInfo } from 'client/utils/token';
import {
  OperationTypeTyping,
  StatementDescription,
  StatementList,
} from 'store/ducks/statements/types';

const transformFirstLetterInUppercase = (value: string) => {
  const lowercased = value.toLowerCase();
  return lowercased.charAt(0).toUpperCase() + lowercased.slice(1);
};

export const getIsIncomingOperation = (operationType: OperationTypeTyping) =>
  operationType === 'DEPOSIT' || operationType === 'BUYING_CURRENCY';

export const getDescriptionLabel = (
  operationType: OperationTypeTyping,
  description: StatementDescription
) => {
  const optionsByTable = {
    WITHDRAW: description === 'WITHDRAW' ? 'Withdraw' : 'Withdraw Reverted',
    DEPOSIT: 'Deposit received',
    BUYING_CURRENCY: 'Exchange buy',
    SELLING_CURRENCY: 'Exchange sell',
  };
  return optionsByTable[operationType];
};

export const formatStatements = (statements: StatementList) => {
  const accountInfo = getAccountInfo();
  const documentIdPrefix = accountInfo ? `${accountInfo.Name}/ID_` : '';

  return statements.map((statement) => {
    const { dateFormatted, timeFormatted } = formatDateTime(statement.Date);
    const { PayerBankCompe, ReceiverBankCompe } = statement;
    const payerBankName = getBankName(PayerBankCompe);
    const receiverBankName = getBankName(ReceiverBankCompe);
    return {
      ...statement,
      Date: dateFormatted,
      Hour: timeFormatted,
      BalanceFormatted: formatCurrency(statement.Balance),
      AmountFormatted: formatCurrency(statement.Amount),
      StatusType: transformFirstLetterInUppercase(statement.StatusType),
      TransferTypeFormatted: getFormattedTransferType(statement.TransferType),
      DocumentNumber: formatUserDocument(statement.DocumentNumber),
      PayerBankName: `${PayerBankCompe} - ${payerBankName}`,
      ReceiverBankName: `${ReceiverBankCompe} - ${receiverBankName}`,
      PayerDocumentIdFormatted: `${documentIdPrefix}${statement.DocumentId}`,
      ReceiverDocumentIdFormatted: `${documentIdPrefix}${statement.DocumentId}`,
      IsIncomingOperation: getIsIncomingOperation(statement.Type),
      DescriptionLabel: getDescriptionLabel(
        statement.Type,
        statement.Description
      ),
      // PayerDocumentIdFormatted: `${documentIdPrefix}${statement.DocumentId}`;statement.PayerBankCompe
      //   ? documentIdPrefix + statement.DocumentId
      //   : '',
      // ReceiverDocumentIdFormatted: statement.ReceiverBankCompe
      //   ? documentIdPrefix + statement.DocumentId
      //   : '',
    };
  });
};

export const prepareStatementsToBeExported = (statements: StatementList) => {
  const accountInfo = getAccountInfo();
  const documentIdPrefix = accountInfo ? `${accountInfo.Name}/ID_` : '';

  return statements.map((statement) => {
    const { dateFormatted, timeFormatted } = formatDateTime(statement.Date);
    const { PayerBankCompe, ReceiverBankCompe } = statement;
    const payerBankName = getBankName(PayerBankCompe);
    const receiverBankName = getBankName(ReceiverBankCompe);
    const document = formatUserDocument(statement.DocumentNumber);
    return {
      id: statement.Id,
      date: dateFormatted,
      hour: timeFormatted,
      description: getDescriptionLabel(statement.Type, statement.Description),
      status: transformFirstLetterInUppercase(statement.StatusType),
      transferType: getFormattedTransferType(statement.TransferType),
      transferId: statement.TransactionId,
      originAccountId:
        statement.Type === 'BUYING_CURRENCY' || statement.Type === 'DEPOSIT'
          ? '-'
          : `${documentIdPrefix}${statement.DocumentId}`,
      originClientId: statement.Type === 'DEPOSIT' ? '-' : statement.DocumentId,
      originClientName: statement.FullName,
      originDocument: document,
      originBank: payerBankName,
      originAgency: statement.PayerAgencyNumber,
      originAccount: statement.PayerAccountNumber,
      destinationAccountId:
        statement.Type === 'BUYING_CURRENCY' || statement.Type === 'DEPOSIT'
          ? `${documentIdPrefix}${statement.DocumentId}`
          : '-',
      destinationClientId:
        statement.Type === 'WITHDRAW' ? '-' : statement.DocumentId,
      destinationClientName: statement.FullName,
      destinationDocument: document,
      destinationBank: receiverBankName,
      destinationAgency: statement.ReceiverAgencyNumber,
      destinationAccount: statement.ReceiverAccountNumber,
      balanceFormatted: formatCurrency(statement.Balance),
      amountFormatted: formatCurrency(statement.Amount),
    };
  });
};

export const splitDateRangeIntoChunks = (
  startDateString: string,
  endDateString: string,
  chunkSize = 5
): string[] => {
  if (startDateString === endDateString) {
    return [startDateString, endDateString];
  }

  const startDate = dayjs(startDateString);
  const endDate = dayjs(endDateString);

  const diffInDays = endDate.diff(startDate, 'days');
  const amountOfChunks = Math.floor(diffInDays / chunkSize);
  const restOfDays = diffInDays % chunkSize;

  const dates = [
    startDate.format('YYYY-MM-DD'),
    ...Array(amountOfChunks)
      .fill(0)
      .map((_, index) => {
        const multiplier = index + 1;
        const nextDate = startDate.add(multiplier * chunkSize, 'day');
        return nextDate.format('YYYY-MM-DD');
      }),
  ];

  if (restOfDays > 0) {
    const lastGotDate = dates[dates.length - 1];
    const lastDate = dayjs(lastGotDate).add(restOfDays, 'days');
    dates.push(lastDate.format('YYYY-MM-DD'));
  }

  return dates;
};

export const getNextDayString = (dateString: string) => {
  let date = dayjs(dateString);
  date = date.date(date.date() + 1);
  return date.format('YYYY-MM-DD');
};
